import React, { Component } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import { PaymentService } from "@services";
import { BULK_PAYMENT_STATUSES, BULK_PAYMENT_JOB_TIMER } from "@constants";
import { Modal, Loader, Image } from "../../../../../components";

import "./ReviewPayment.scss";

let intervalRef;

export class ReviewPayment extends Component {
	constructor(props) {
		super(props);
		this.state = {
			show: false,
			status: "pending", // values: pending, loading, success
			transactionId: -1,
		};

		this.initialState = { ...this.state };
	}

	componentWillUnmount() {
		if (intervalRef) {
			clearInterval(intervalRef);
		}
	}

	openModal = () => {
		this.setState({ show: true });
	};

	onCancel = () => {
		const { status } = this.state;
		const { push } = this.props;

		this.setState(this.initialState, () => {
			if (status !== "pending") {
				// requires extra time in order to first close/rest the modal state, then navigate to requested page
				setTimeout(() => {
					push("/payments/list/payment");
					clearTimeout();
				}, 500);
			}
		});
	};

	resetModal = () => this.setState(this.initialState);

	onViewReceipt = () => {
		const { transactionId } = this.state;
		const { push } = this.props;

		this.setState(this.initialState, () => {
			// requires extra time in order to first close/rest the modal state, then navigate to requested page
			setTimeout(() => {
				push({ pathname: `/payments/receipt/${transactionId}`, query: { paid: true } });
				clearTimeout();
			}, 500);
		});
	};

	/**
	 * Determines if a the selected invoices qualify for bulk payments
	 * @param {Number} jobId The unique id of the job whos inovice is being paid
	 */
	checkBulkPaymentStatus = jobId => {
		const { lang } = global;

		const checkPaymentRequest = () => {
			PaymentService.verifyBulkPaymentStatus(jobId)
				.then(({ data }) => {
					const StatusId = data?.StatusId ?? -1;

					if (StatusId === BULK_PAYMENT_STATUSES.SUCCEEDED) {
						const transactionId = data?.TransactionId ?? -1;

						clearInterval(intervalRef);
						this.setState({ status: "success", transactionId });

						utils.notify({
							type: "success",
							message: lang.t("Payment complete"),
						});
					} else if (StatusId === BULK_PAYMENT_STATUSES.FAILED) {
						clearInterval(intervalRef);
						this.setState({ status: "pending" });

						utils.notify({
							type: "error",
							title: lang.t("Payment Failed"),
							message: lang.t("The invoices payment has failed"),
						});
					}
				})
				.catch(({ response: { data } }) => {
					utils.notify({
						type: "error",
						title: "Error",
						message:
							data?.Message ??
							lang.t("Error getting the current status of the payment"),
					});
				});
		};

		intervalRef = setInterval(checkPaymentRequest, BULK_PAYMENT_JOB_TIMER);
	};

	/** Calls an API endpoint to attempt to pay each invoice */
	onMakePayment = () => {
		const { lang } = global;
		const { total, activeInvoiceIds, paymentMethodId } = this.props;

		this.setState({ status: "loading" });

		PaymentService.makePayment(paymentMethodId, total, activeInvoiceIds)
			.then(({ data }) => {
				this.checkBulkPaymentStatus(data.JobId);
			})
			.catch(({ response: { data } }) => {
				this.setState({ status: "pending" });
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("The invoice could not be paid"),
				});
			});
	};

	render() {
		const { show, status } = this.state;
		const { subTotal, taxes, total, activeInvoiceIds } = this.props;

		const { lang } = global;

		const modalTitle = () => {
			let title;
			switch (status) {
				case "success":
					if (activeInvoiceIds.length > 1) title = lang.t("Invoices Paid");
					else {
						title = lang.t("Invoice Paid");
					}
					break;
				default:
					title = lang.t("Review Payment");
					break;
			}
			return title;
		};

		return (
			<Modal
				show={show}
				onHide={this.onCancel}
				onExited={() => this.resetModal()}
				id="review-payment-modal"
			>
				<Modal.Header closeButton>
					<Modal.Title>{modalTitle()}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="text-center">
					<Choose>
						<When condition={status === "pending"}>
							<div id="review-payment-summary">
								<div className="row">
									<div className="col-xs-12">
										<h4>{lang.t("Order Summary")}</h4>
									</div>
								</div>
								<div className="row-separator" />
								<div className="row">
									<div className="col-xs-6 text-right">{lang.t("Subtotal")}</div>
									<div className="col-xs-6 text-left">
										{utils.currencyFormatter(subTotal)}
									</div>
								</div>
								<div className="row-separator" />
								<div className="row">
									<div className="col-xs-6 text-right">
										{lang.t("Fees and Taxes")}
									</div>
									<div className="col-xs-6 text-left">
										{utils.currencyFormatter(taxes)}
									</div>
								</div>
								<div className="row-separator row-separator-onlyTop" />
								<div
									id="review-payment-total-row"
									className="row"
								>
									<div className="col-xs-6 text-right">
										{lang.t("Total").toUpperCase()}
									</div>
									<div
										id="review-payment-total"
										className="col-xs-6 text-left"
									>
										<strong>{utils.currencyFormatter(total)}</strong>
									</div>
								</div>
								<div id="review-payment-pay-btn">
									<button
										type="button"
										className="btn btn-primary"
										onClick={this.onMakePayment}
									>
										{lang.t("Make Payment")}
									</button>
								</div>
							</div>
						</When>
						<When condition={status === "loading"}>
							<div id="review-payment-loading">
								<div>
									<Loader
										active
										spinnerOnly
									/>
								</div>
								<h4 id="review-payment-loading-title">
									{lang.t("Your payment is being processed")}
								</h4>
								<div id="review-payment-loading-message">
									<div>
										{lang.t(
											"You will be notified via email when the process is complete_",
										)}
									</div>
									<div>
										{lang.t(
											"You can close this window or wait until the payment finishes_",
										)}
									</div>
									<div>{`${lang.t("Thank you for using")} RKPay`}</div>
								</div>
								<div id="review-payment-loading-action-btn">
									<button
										type="button"
										className="btn btn-primary"
										onClick={this.onCancel}
									>
										{lang.t("Done")}
									</button>
								</div>
							</div>
						</When>
						<When condition={status === "success"}>
							<div id="review-payment-success">
								<div id="review-payment-success-checkmark">
									<Image src="/images/checkmark.png" />
								</div>
								<div id="review-payment-success-title">
									<h4>{lang.t("Payment was successful")}</h4>
								</div>
								<div id="review-payment-success-message">
									<div>
										{lang.t("We have successfully processed your payment_")}
									</div>
									<div>{`${lang.t("Thank you for using")} RKPay`}</div>
								</div>
								<div id="review-payment-success-action-btn">
									<button
										type="button"
										className="btn btn-primary"
										onClick={this.onViewReceipt}
									>
										{lang.t("View Receipt")}
									</button>
								</div>
							</div>
						</When>
					</Choose>
				</Modal.Body>
				<If condition={status === "pending"}>
					<Modal.Footer>
						<small>
							By placing your order, you agree to RollKall&#39;s&nbsp;
							<a
								href="https://www.rollkall.com/privacy"
								target="_blank"
								rel="noreferrer"
							>
								Privacy Policy
							</a>
							&nbsp;and&nbsp;
							<a
								href="https://www.rollkall.com/terms"
								target="_blank"
								rel="noreferrer"
							>
								Terms of Service
							</a>
						</small>
					</Modal.Footer>
				</If>
			</Modal>
		);
	}
}

ReviewPayment.propTypes = {
	subTotal: PropTypes.number,
	taxes: PropTypes.number,
	total: PropTypes.number,
	activeInvoiceIds: PropTypes.array,
	paymentMethodId: PropTypes.string,
	push: PropTypes.func.isRequired,
};

ReviewPayment.defaultProps = {
	subTotal: 0,
	taxes: 0,
	total: 0,
	activeInvoiceIds: [],
	paymentMethodId: null,
};

export default ReviewPayment;
